// @flow
import { useState } from 'react';
import { Avatar, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { FunnelSimple } from '@phosphor-icons/react';
import { DataGrid, DataGridColumn, NoData, useQaAuditsQuery } from '../../../shared';
import { CallQaFilterInput } from '@coverright/data-access/types/enrollment';
import { SearchInput } from '@coverright/ui/inputs';
import moment from 'moment';
import _ from 'lodash';
import { ScoreText } from '../ui/qa-shared';
import { AiBadge } from '../../../shared';
import { useCallDrawer } from '../ui/call-drawer-by-aircall';
import { withQaFilterDrawer, useQaFilterDrawer } from '../ui/qa-filter-drawer';

type Row = NonNullable<ReturnType<typeof useQaAuditsQuery>['data']>['qaAudits']['data'][0];

const AuditsTable = () => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CallQaFilterInput>({});
  const { open } = useCallDrawer();
  const { open: openFilters } = useQaFilterDrawer();

  const { data, loading } = useQaAuditsQuery({
    variables: { filter: { ...filter, searchTerm: search || undefined }, page: { page, size } },
    fetchPolicy: 'cache-and-network',
  });

  const rows = data?.qaAudits.data || [];
  const total = Number(data?.qaAudits.totalElements || 0);

  const columns: DataGridColumn<Row, never>[] = [
    { title: 'Call date', value: r => r.callDate ? moment(r.callDate).format('L LT') : '-' },
    {
      title: 'Agent',
      value: r => (
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Avatar sx={{ width: 26, height: 26, bgcolor: '#2d969f', fontSize: 11 }}>
            {(r.agentName || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
          </Avatar>
          <Typography variant={'body2'}>{r.agentName || '-'}</Typography>
        </Stack>
      ),
    },
    { title: 'Client', value: r => <Typography variant={'body2'}>{r.clientName || '-'}</Typography> },
    { title: 'Score', value: r => <ScoreText score={r.effectiveScore} variant={'body2'} /> },
    { title: 'Disposition', value: r => <Chip size={'small'} label={_.upperFirst(r.dispositionClass)} sx={{ fontSize: 11 }} /> },
    { title: 'Enrollment', value: r => r.isEnrollmentCall ? <Chip size={'small'} color={'success'} label={'Yes'} sx={{ fontSize: 11 }} /> : '-' },
    { title: 'Review', value: r => r.wasOverridden ? <AiBadge label={'Edited'} /> : <Typography variant={'caption'} color={'text.secondary'}>AI</Typography> },
  ];

  return (
    <Box>
      <Stack direction={'row'} alignItems={'center'} gap={2} mb={2}>
        <SearchInput value={search} loading={loading}
                     onChange={(v: string) => { setSearch(v); setPage(0); }}
                     placeholder={'Search client, agent...'} />
        <Button variant={'outlined'}
                onClick={() => openFilters(f => { setFilter(f); setPage(0); }, filter)}
                startIcon={<FunnelSimple weight={'bold'} />}
                size={'small'} sx={{ minWidth: 90, height: 40 }}>
          Filters
        </Button>
        <Box flex={1} />
        <Typography variant={'caption'} color={'text.secondary'}>
          {total ? `${page * size + 1}-${Math.min((page + 1) * size, total)} of ${total}` : ''}
        </Typography>
      </Stack>
      {!loading && !rows.length
        ? <NoData>No audited calls match the filter.</NoData>
        : <DataGrid
            columns={columns}
            rows={rows}
            loading={loading}
            page={page}
            rowsPerPage={size}
            total={total}
            onPageChange={setPage}
            onRowsPerPageChange={setSize}
            onRowClick={r => open(r.airCallId)}
          />}
    </Box>
  );
};

export const Audits = withQaFilterDrawer(AuditsTable);